<script setup lang="ts">
import { i18n } from "@/i18n";
import { computed, ref } from "vue";
import AppIcon from "@/components/ui-kit/icon/AppIcon.vue";
import AppLoader from "@/components/common/AppLoader.vue";
import { Job, jobsStore } from "@/store/jobs";
import { JobStatus } from "@/models/etc/types";
import { getJobDeadlineState, parseJobDeadline } from "@/utils/jobDeadline";
import AppTooltip from "@/components/ui-kit/tooltip/AppTooltip.vue";

const props = defineProps<{
  job: Job;
}>();

const emit = defineEmits<{
  (e: "select-job", job: Job): void;
}>();

const store = jobsStore();
const isLoading = ref(false);

const onCardClick = () => {
  emit("select-job", props.job);
};

const showCreatorName = computed(
  () => !!props.job.Creator && props.job.Creator.Id !== props.job.Worker?.Id
);

const getJobStatusBarColor = (status: JobStatus) => {
  switch (status) {
    case JobStatus.Standard:
      return "#9A9C9E";
    case JobStatus.Working:
      return "#86CED2";
    case JobStatus.WaitingForCheck:
      return "#FFE7AA";
    case JobStatus.Completed:
      return "#6DA279";
    case JobStatus.Lost:
      return "#EE966A";
  }
};

const toDate = (date?: string | Date | null) => {
  if (!date) {
	return null;
  }
  return typeof date === "string" ? new Date(date) : date;
};

const formatDate = (date?: string | Date | null) => {
  const dateObj = toDate(date);
  if (!dateObj) {
	return "";
  }

  return dateObj.toLocaleDateString("ru-RU", {
    day: "2-digit",
    month: "2-digit",
    year: "2-digit",
  });
};

const formatTimeDate = (date?: string | Date) => {
  const dateObj = toDate(date);
  if (!dateObj) {
    return "";
  }

  const hours = String(dateObj.getHours()).padStart(2, "0");
  const minutes = String(dateObj.getMinutes()).padStart(2, "0");
  return `${hours}:${minutes} ${formatDate(dateObj)}`;
};

const loaderStyle = computed(() => ({
  "background-color": "rgba(255, 255, 255, 0.5)",
}));

const dateState = computed(() => getJobDeadlineState(props.job));
const isDateLost = computed(() => dateState.value === "lost");
const isDateSuccess = computed(() => dateState.value === "success");

const dateTooltip = computed(() => {
  if (!props.job.CompletionDate) {
    return String(i18n.global.t('job_deadline'));
  }
  // У завершённой работы на месте срока показана дата завершения, поэтому
  // исходный срок выносим в тултип
  return isDateLost.value
    ? String(i18n.global.t('job_overdue_at', { date: formatDate(parseJobDeadline(props.job.ToDoDate)) }))
    : String(i18n.global.t('job_state_completed'));
});

const additionsExpanded = ref(false);
</script>

<template>
  <div
    ref="$job"
    :class="{
      'jobs-panel-list-item': true,
    }"
    @click="onCardClick"
  >
    <app-loader v-if="isLoading" :style="loaderStyle" />
    <div
      :class="{
        'jobs-panel-list-item__drop-zone': true,
      }"
    />
    <div
      class="jobs-panel-list-item__colored"
      :style="{ backgroundColor: getJobStatusBarColor(props.job.Status) }"
    />
    <div class="jobs-panel-list-item__header">
      <div class="jobs-panel-list-item__header-workers">
        <div class="jobs-panel-list-item__header-worker">
          {{ props.job.Worker.Name }}
        </div>
        <template v-if="showCreatorName">
          <div class="jobs-panel-list-item__header-divider">
            <app-icon
              class="jobs-panel-list-item__header-options-btn"
              name="chevron-left"
              width="16px"
              color="#499259"
            />
          </div>
          <div class="jobs-panel-list-item__header-worker">
            {{ props.job.Creator.Name }}
          </div>
        </template>
      </div>
    </div>
    <div class="jobs-panel-list-item__body">
      <div
        class="jobs-panel-list-item__body-message"
        v-html="props.job.Message"
      />
    </div>
    <div class="jobs-panel-list-item__footer">
      <div class="jobs-panel-list-item__footer-bottom">
        <app-tooltip :text="dateTooltip">
          <template #activator>
            <div
              :class="{
                'jobs-panel-list-item__footer-bottom-date': true,
                'jobs-panel-list-item__footer-bottom-date--lost': isDateLost,
                'jobs-panel-list-item__footer-bottom-date--success': isDateSuccess,
              }"
            >
              <app-icon
                class="jobs-panel-list-item__footer-bottom-date-clock"
                name="clock"
                width="16px"
              />
              <template v-if="props.job.CompletionDate">
                {{ formatTimeDate(props.job.CompletionDate) }}
              </template>
              <template v-else-if="props.job.ToDoDate">
                {{ formatDate(parseJobDeadline(props.job.ToDoDate)) }}
              </template>
            </div>
          </template>
        </app-tooltip>
        <span class="jobs-panel-list-item__footer-bottom-created">{{ formatTimeDate(props.job.CreateTime) }}</span>
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
@import "@/styles/color.scss";

.jobs-panel-list-item {
  display: flex;
  flex-direction: column;
  position: relative;
  padding: 0 0px 0 8px;
  color: #4d4d4d;
  border: 1px dashed transparent;
  border-left: none !important;
  border-radius: 8px;
  cursor: pointer;

  &:hover {
    background: $color-divider;
  }

  &__colored {
    position: absolute;
    left: 0;
    top: 0;
    bottom: 0;
    width: 4px;
    border-top-left-radius: 8px 12px;
    border-bottom-left-radius: 8px 12px;
  }

  &__header {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    margin-bottom: 4px;

    &-workers {
      display: flex;
      align-items: center;
      min-width: 0;
      color: #438451;
      font-weight: 500;
      font-size: 12px;
      line-height: 16px;
      overflow: hidden;
    }

    &-worker {
      display: block;
      cursor: pointer;
      white-space: nowrap;
      flex-shrink: 0;

      &:last-child {
        flex-shrink: 1;
        min-width: 0;
        overflow: hidden;
        text-overflow: ellipsis;
      }
    }

    &-divider {
      display: flex;
      flex-shrink: 0;
      align-items: center;
    }
  }

  &__body {
    display: flex;
    flex-direction: column;
    margin-bottom: 4px;

    &-message {
      font-size: 12px;
      line-height: 16px;
      color: $color-text-main;
      margin-bottom: 2px;

      display: -webkit-box;
      -webkit-line-clamp: 2;
      -webkit-box-orient: vertical;
      overflow: hidden;
      text-overflow: ellipsis;
    }

    &-title {
      font-weight: 500;
      color: #4d4d4d;
    }
  }

  &__footer {
    display: flex;
    flex-direction: column;
    max-width: 100%;
    &-bottom {
      display: flex;
      align-items: flex-end;
      justify-content: space-between;
      font-size: 12px;
      line-height: 16px;
      color: #979797;

      :deep(.app-tooltip__activator) {
        display: flex;
      }

      &-created {
        color: #979797;
        font-size: 12px;
        line-height: 16px;
      }

      &-date {
        display: flex;
        align-items: center;
        color: #979797;
        font-size: 12px;
        line-height: 16px;

        &--lost {
          color: #ee966a !important;

          svg {
            path {
              color: #ee966a !important;
            }
          }
        }

        &--success {
          color: #6da279 !important;

          svg {
            path {
              color: #6da279 !important;
            }
          }
        }

        &-clock {
          cursor: auto;
          flex-shrink: 0;
          margin-top: -2px;
          margin-right: 4px;

          &:hover {
            background: transparent;
          }
        }
      }
    }
  }
}
</style>
